import React from 'react';
import {StyleSheet, View} from 'react-native';
import type {NativeStackScreenProps} from '@react-navigation/native-stack';
import {useQuery} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';

import {useAuthStore} from '@/entities/session/auth-store';
import type {PersonnelStackParamList} from '@/app/routes';
import {fetchPersonnel} from '@/entities/personnel/api';
import {PersonnelForm} from '@/features/manage-personnel/ui/PersonnelForm';
import {EmptyState} from '@/shared/ui/empty-state';
import {Page} from '@/shared/ui/page';
import {useGoUp} from '@/shared/lib/use-page-nav';
import {Skeleton} from '@/shared/ui/skeleton';
import {radii, spacing} from '@/shared/ui/theme';

/**
 * «Сотрудники» → форма создания или правки.
 *
 * Для правки сперва грузится ПОЛНАЯ запись (с фото и полями терминала), и только
 * потом монтируется форма: её состояние заполняется из записи в момент монтирования,
 * значит запись должна существовать раньше.
 */
type Props = NativeStackScreenProps<PersonnelStackParamList, 'PersonnelForm'>;

const styles = StyleSheet.create({
  loading: {gap: spacing(4)},
});

export function PersonnelFormScreen({route, navigation}: Props) {
  const {t} = useTranslation();
  const goUp = useGoUp();
  const companyId = useAuthStore(s => s.activeContext?.companyId);
  const recordId = route.params?.recordId;

  const query = useQuery({
    queryKey: ['personnel', companyId, recordId],
    queryFn: () => fetchPersonnel(companyId as string, recordId as string),
    enabled: !!companyId && !!recordId,
  });

  if (!companyId) {
    return null;
  }

  let body: React.ReactNode;
  if (recordId && query.isPending) {
    body = (
      <View style={styles.loading}>
        <Skeleton height={230} radius={radii.xl} />
        <Skeleton height={140} radius={radii.xl} />
      </View>
    );
  } else if (recordId && query.isError) {
    body = (
      <EmptyState
        tone="error"
        title={t('home.person.notFound')}
        actionLabel={t('actions.retry')}
        onAction={() => void query.refetch()}
      />
    );
  } else {
    body = (
      <PersonnelForm
        companyId={companyId}
        record={recordId ? query.data : undefined}
        onSaved={goUp}
        // `replace`, а не `navigate`: оператора отправляют в карточку, которая уже
        // держит введённый номер, и оставлять отвергнутую форму в стеке позади —
        // значит разрешить «назад» вернуться к черновику, который не сохранится.
        onOpenConflict={ownerRecordId =>
          navigation.replace('PersonnelCard', {
            recordId: ownerRecordId,
            name: t('personnel.numberTakenOwner'),
          })
        }
      />
    );
  }

  return (
    <Page
      scroll
      // Две колонки — главное и сбоку (фото, срок доступа) — см. `PersonnelForm`.
      width="wide"
      title={t(recordId ? 'personnel.editTitle' : 'personnel.createTitle')}
      testID="staff-form">
      {body}
    </Page>
  );
}
